import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DependencyResponse } from '@/features/items/api/dependencies';
import { invalidateAfterDependencyChange } from '@/features/items/utils/itemCacheUtils';
import { InvalidDependencyPairError, SelfDependencyError } from './dependencyErrors';
import { createDependency } from './dependencyOperations';

interface CreateDependencyVariables {
  predecessorId: number;
  successorId: number;
}

interface CreateDependencyResult {
  response: DependencyResponse;
  otherId: number;
}

interface UseCreateDependencyMutationParams {
  currentItemId: number;
  options?: Omit<
    UseMutationOptions<CreateDependencyResult, Error, CreateDependencyVariables>,
    'mutationFn'
  >;
}

export const useCreateDependencyMutation = ({
  currentItemId,
  options,
}: UseCreateDependencyMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<CreateDependencyResult, Error, CreateDependencyVariables>({
    ...options,
    mutationFn: async ({ predecessorId, successorId }) => {
      const isPredecessorCurrent = predecessorId === currentItemId;
      const isSuccessorCurrent = successorId === currentItemId;
      if (isPredecessorCurrent && isSuccessorCurrent) {
        throw new SelfDependencyError();
      }
      if (!isPredecessorCurrent && !isSuccessorCurrent) {
        throw new InvalidDependencyPairError();
      }
      const otherId = isPredecessorCurrent ? successorId : predecessorId;
      const actualPredecessorId = isPredecessorCurrent ? currentItemId : otherId;
      const actualSuccessorId = isPredecessorCurrent ? otherId : currentItemId;
      const response = await createDependency({
        itemId: currentItemId,
        predecessorId: actualPredecessorId,
        successorId: actualSuccessorId,
      });
      return { response, otherId };
    },
    onSuccess: (data, variables, ...rest) => {
      const { otherId } = data;
      // Defer invalidation to the next microtask so the caller's UI updates
      // (e.g. closing the add picker) commit before suspense queries refetch.
      queueMicrotask(() => invalidateAfterDependencyChange(queryClient, currentItemId, otherId));
      options?.onSuccess?.(data, variables, ...rest);
    },
  });
};
